/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationUserSessionIssuersMigrate } from "../funcs/organizationUserSessionIssuersMigrate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { MigrateOrganizationUserSessionIssuerResult } from "../models/components/migrateorganizationusersessionissuerresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  MigrateOrganizationUserSessionIssuerRequest,
  MigrateOrganizationUserSessionIssuerSecurity,
} from "../models/operations/migrateorganizationusersessionissuer.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type MigrateOrganizationUserSessionIssuerMutationVariables = {
  request: MigrateOrganizationUserSessionIssuerRequest;
  security?: MigrateOrganizationUserSessionIssuerSecurity | undefined;
  options?: RequestOptions;
};

export type MigrateOrganizationUserSessionIssuerMutationData =
  MigrateOrganizationUserSessionIssuerResult;

export type MigrateOrganizationUserSessionIssuerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * migrateIssuer organizationUserSessionIssuers
 *
 * @remarks
 * Consolidate a source user_session_issuer onto a target issuer, preserving clients, sessions, attachments, and remote-session credentials before soft-deleting the source. The target must be in the same project or a broader organization scope. Access tokens issued through the authorization_code and refresh_token grants are audience-bound to the issuer, so once a repointed MCP server verifies bearers against the target, outstanding access tokens minted under the source are rejected until the client refreshes. Migrated sessions and refresh tokens keep working, so the refresh succeeds without re-authorization. Tokens from the jwt-bearer (ID-JAG) grant are bound to the resource URL and are unaffected. Requires org:admin.
 */
export function useMigrateOrganizationUserSessionIssuerMutation(
  options?: MutationHookOptions<
    MigrateOrganizationUserSessionIssuerMutationData,
    MigrateOrganizationUserSessionIssuerMutationError,
    MigrateOrganizationUserSessionIssuerMutationVariables
  >,
): UseMutationResult<
  MigrateOrganizationUserSessionIssuerMutationData,
  MigrateOrganizationUserSessionIssuerMutationError,
  MigrateOrganizationUserSessionIssuerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildMigrateOrganizationUserSessionIssuerMutation(client, options),
    ...options,
  });
}

export function mutationKeyMigrateOrganizationUserSessionIssuer(): MutationKey {
  return ["@gram/client", "organizationUserSessionIssuers", "migrate"];
}

export function buildMigrateOrganizationUserSessionIssuerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: MigrateOrganizationUserSessionIssuerMutationVariables,
  ) => Promise<MigrateOrganizationUserSessionIssuerMutationData>;
} {
  return {
    mutationKey: mutationKeyMigrateOrganizationUserSessionIssuer(),
    mutationFn: function migrateOrganizationUserSessionIssuerMutationFn({
      request,
      security,
      options,
    }): Promise<MigrateOrganizationUserSessionIssuerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationUserSessionIssuersMigrate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
